"use client";

import React from "react";
import { Calendar, User, ArrowLeft, Share2 } from "lucide-react";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Link } from "@/components/common/Link";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { AspectRatio } from "@/components/ui/aspect-ratio";


declare const wvcClient: any;

function RelatedPostsConsumer() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {[...Array(3)].map((_, i) => (
          <Card key={i} data-index={i} className="animate-pulse bg-card/50 border-border/50 rounded-lg">
            <div className="h-48 bg-muted/50 rounded-t-lg" />
            <CardHeader>
              <div className="h-6 bg-muted/50 rounded w-3/4 mb-2" />
            </CardHeader>
            <CardContent>
              <div className="h-4 bg-muted/50 rounded w-full mb-2" />
              <div className="h-4 bg-muted/50 rounded w-2/3" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (error || !posts || posts.length === 0) {
    return null;
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      {posts.map((post, i) => (
        <Card key={post.id} data-index={i} className="flex flex-col overflow-hidden bg-card border-border hover:border-primary/30 transition-colors shadow-sm rounded-lg">
          {post.featuredImage && (
            <Link to={post.link || "#"} className="block overflow-hidden">
              <AspectRatio ratio={16 / 9}>
                <img
                  src={post.featuredImage}
                  alt={post.title}
                  className="object-cover w-full h-full transition-transform hover:scale-105 duration-500"
                />
              </AspectRatio>
            </Link>
          )}
          <CardHeader>
            <Link to={post.link || "#"}>
              <CardTitle className="font-default text-xl line-clamp-2 hover:text-primary transition-colors text-foreground">
                {post.title}
              </CardTitle>
            </Link>
          </CardHeader>
          <CardContent className="flex-grow">
            <p className="text-muted-foreground text-sm line-clamp-3 font-sans">
              {post.excerpt?.replace(/(<([^>]+)>)/gi, "") || "No excerpt available."}
            </p>
          </CardContent>
          <CardFooter>
            <Link to={post.link || "#"} className="w-full">
              <Button variant="outline" className="w-full border-primary/20 text-primary hover:bg-primary/5 rounded-md">Read Article</Button>
            </Link>
          </CardFooter>
        </Card>
      ))}
    </div>
  );
}

function RelatedPostsSection() {
  
  const wp_query = new WP_Query({ post_type: "post", per_page: 3 });

  return (
    <div className="mt-24">
      <div className="flex items-center justify-between mb-8">
        <h2 className="text-3xl font-default font-bold text-foreground">Pet Care Tips</h2>
        <Link to="/WellnessServicesPage">
          <Button variant="ghost" className="text-muted-foreground hover:text-primary font-medium">
            View all
          </Button>
        </Link>
      </div>
      <PostsProvider wp_query={wp_query}>
        <RelatedPostsConsumer />
      </PostsProvider>
    </div>
  );
}

function SinglePostConsumer() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto animate-pulse">
        <div className="h-8 w-24 bg-muted/50 rounded mb-8" />
        <div className="h-12 md:h-16 bg-muted/50 rounded w-3/4 mx-auto mb-6" />
        <div className="h-6 bg-muted/50 rounded w-1/2 mx-auto mb-12" />
        <div className="h-[400px] bg-muted/50 rounded-lg mb-12" />
        <div className="space-y-4">
          <div className="h-4 bg-muted/50 rounded w-full" />
          <div className="h-4 bg-muted/50 rounded w-full" />
          <div className="h-4 bg-muted/50 rounded w-5/6" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto text-center py-20">
        <h2 className="text-2xl font-default font-bold text-destructive mb-4">Error Loading Post</h2>
        <p className="text-muted-foreground font-sans">{error}</p>
        <Link to="/HomePage">
          <Button className="mt-6 bg-primary text-primary-foreground rounded-md">Return Home</Button>
        </Link>
      </div>
    );
  }

  if (!posts || posts.length === 0) {
    return (
      <div className="max-w-4xl mx-auto text-center py-20">
        <h2 className="text-2xl font-default font-bold text-foreground mb-4">Article Not Found</h2>
        <p className="text-muted-foreground font-sans">The pet care guide you are looking for does not exist or has been moved.</p>
        <Link to="/HomePage">
          <Button className="mt-6 bg-primary text-primary-foreground rounded-md">Return Home</Button>
        </Link>
      </div>
    );
  }

  const post = posts[0];

  return (
    <article className="max-w-4xl mx-auto">
      {}
      <div className="mb-8">
        <Link to="/WellnessServicesPage">
          <Button variant="ghost" size="sm" className="text-muted-foreground hover:text-primary -ml-3 font-medium">
            <ArrowLeft className="mr-2 size-4" />
            Back to Pet Care Blog
          </Button>
        </Link>
      </div>

      {}
      <header className="mb-12 text-center">
        {post.categories && post.categories.length > 0 && (
          <div className="mb-6 flex justify-center gap-2 flex-wrap">
            {post.categories.map((cat, i) => (
              <span key={cat.id} data-index={i} className="px-3 py-1 bg-secondary text-accent-foreground text-xs font-semibold rounded-lg uppercase tracking-wider">
                {cat.name}
              </span>
            ))}
          </div>
        )}
        
        <h1 className="text-4xl md:text-5xl lg:text-6xl font-default font-bold text-foreground mb-6 leading-tight">
          {post.title}
        </h1>
        
        <div className="flex items-center justify-center gap-6 text-muted-foreground text-sm font-sans">
          {post.author && (
            <div className="flex items-center gap-2">
              <Avatar className="size-6 border border-border">
                <AvatarImage src={post.author.avatar?.["24"]} alt={post.author.name} />
                <AvatarFallback className="bg-muted text-xs">{post.author.name.charAt(0)}</AvatarFallback>
              </Avatar>
              <span className="font-medium text-foreground">{post.author.name}</span>
            </div>
          )}
          {post.date && (
            <div className="flex items-center gap-2">
              <Calendar className="size-4" />
              <span>{new Date(post.date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</span>
            </div>
          )}
        </div>
      </header>

      {}
      {post.featuredImage && (
        <div className="mb-16 rounded-lg overflow-hidden shadow-lg border border-border/50">
          <AspectRatio ratio={16 / 9}>
            <img 
              src={post.featuredImage} 
              alt={post.title} 
              className="object-cover w-full h-full"
            />
          </AspectRatio>
        </div>
      )}

      {}
      <div className="prose prose-lg max-w-none mb-16 font-sans text-muted-foreground leading-relaxed">
        {post.content ? (
          <div dangerouslySetInnerHTML={{ __html: post.content }} />
        ) : (
          <p>No content available for this post.</p>
        )}
      </div>

      {}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 py-6 border-y border-border mb-16">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-medium text-foreground mr-2">Topics:</span>
          {post.tags && post.tags.length > 0 ? (
            post.tags.map((tag, i) => (
              <Link key={tag.id} data-index={i} to={tag.link || "#"}>
                <span className="px-3 py-1 bg-muted text-muted-foreground text-xs rounded-md hover:bg-secondary hover:text-accent-foreground transition-colors">
                  {tag.name}
                </span>
              </Link>
            ))
          ) : (
            <span className="text-sm text-muted-foreground">General Care</span>
          )}
        </div>
        <Button variant="outline" size="sm" className="gap-2 border-primary/20 text-primary hover:bg-primary/5 rounded-md">
          <Share2 className="size-4" />
          Share Guide
        </Button>
      </div>

      {}
      {post.author && (
        <div className="flex flex-col sm:flex-row items-center sm:items-start gap-6 bg-card border border-border p-8 rounded-lg mb-16 shadow-sm">
          <Avatar className="size-24 border-2 border-primary/10">
            <AvatarImage src={post.author.avatar?.["96"]} alt={post.author.name} />
            <AvatarFallback className="text-2xl bg-muted text-muted-foreground">{post.author.name.charAt(0)}</AvatarFallback>
          </Avatar>
          <div className="text-center sm:text-left flex-1">
            <h3 className="text-xl font-default font-bold text-foreground mb-2">
              Written by {post.author.name}
            </h3>
            <p className="text-muted-foreground mb-4 font-sans">
              Veterinary specialist at City Pet Care. Dedicated to providing the best medical advice and compassionate care for your furry family members.
            </p>
            <Link to={post.author.link || "#"}>
              <Button variant="link" className="p-0 h-auto text-primary font-semibold hover:no-underline">
                View all articles by {post.author.name} →
              </Button>
            </Link>
          </div>
        </div>
      )}

      <Separator className="my-12 bg-border/50" />

      {}
      <RelatedPostsSection />
    </article>
  );
}

export default function SinglePostTemplateSection() {
  
  const currentWPQueryParams = typeof window !== 'undefined' && wvcClient ? wvcClient.getCurrentWPQueryParams() : { post_type: 'post' };
  const wp_query = new WP_Query(currentWPQueryParams);

  return (
    <section data-section-id="237132" 
      id="single-post-template-section" 
      className="bg-background text-foreground py-16 md:py-24 px-4 md:px-8 min-h-screen"
    >
      <div className="container mx-auto">
        <PostsProvider wp_query={wp_query}>
          <SinglePostConsumer />
        </PostsProvider>
      </div>
    </section>
  );
}